import { useEffect, useState, useMemo } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { useAuth } from '../../contexts/AuthContext';
import { FlaskConical, Plus, Trash2, Save, RotateCcw, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function MailClassifierRulesPage({ embedded = false, onBack } = {}) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [test, setTest] = useState({
    senderEmail: '', senderName: '', subject: '', body: '',
    result: null, loading: false, error: null,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/mail-classifier-rules');
      if (!r.ok) throw new Error('Laden fehlgeschlagen');
      const d = await r.json();
      setData(d);
      setDraft(JSON.parse(JSON.stringify(d.active)));
    } catch (e) {
      setFeedback({ kind: 'error', msg: e.message || 'Fehler' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const isDirty = useMemo(() => {
    if (!data) return false;
    return JSON.stringify(draft) !== JSON.stringify(data.active);
  }, [data, draft]);

  if (!user) return null;
  if (loading || !data) return <div className="p-6 text-sm text-gray-500">Lädt …</div>;

  const labels = data.labels;

  const update = (idx, patch) => {
    const next = [...draft];
    next[idx] = { ...next[idx], ...patch };
    setDraft(next);
  };
  const remove = (idx) => setDraft(draft.filter((_, i) => i !== idx));
  const addRule = () => setDraft([...draft, { field: 'subject', pattern: '', kind: 'general', reason: '' }]);

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await authFetch('/api/mail-classifier-rules', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: draft }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Speichern fehlgeschlagen');
      setFeedback({ kind: 'ok', msg: 'Regeln gespeichert — wirkt sofort für neue Mails.' });
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', msg: String(e.message || e) });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Alle Regeln auf Werks-Defaults zurücksetzen?')) return;
    setSaving(true); setFeedback(null);
    try {
      const r = await authFetch('/api/mail-classifier-rules/reset', { method: 'POST' });
      if (!r.ok) throw new Error('Reset fehlgeschlagen');
      setFeedback({ kind: 'ok', msg: 'Werks-Defaults wiederhergestellt.' });
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', msg: String(e.message || e) });
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTest(s => ({ ...s, loading: true, error: null }));
    try {
      const r = await authFetch('/api/mail-classifier-rules/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderEmail: test.senderEmail, senderName: test.senderName,
          subject: test.subject, body: test.body,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Fehler');
      setTest(s => ({ ...s, result: j, loading: false }));
    } catch (e) {
      setTest(s => ({ ...s, error: String(e.message || e), loading: false }));
    }
  };

  return (
    <div className={embedded ? '' : 'p-6 max-w-6xl mx-auto'} data-testid="mail-classifier-rules-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          {onBack && (
            <button onClick={onBack}
                    className="text-xs text-gray-500 hover:text-[#9ec043] inline-flex items-center gap-1 mb-1"
                    data-testid="rules-back">
              <ArrowLeft className="w-3 h-3" /> Zurück zu Anfragen
            </button>
          )}
          <h1 className="text-xl font-bold text-[#32373c]">Mail-Klassifizierungs-Regeln</h1>
          <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
            Hart-Regeln werden VOR dem LLM ausgewertet. Match → deterministische Kategorie
            (Confidence 100). Kein Match → LLM klassifiziert; Confidence &lt; 60 % wird auf
            „Allgemein" umgeleitet (manuell prüfen). Änderungen wirken sofort für neue Mails.
          </p>
          {data.meta?.isOverridden && (
            <p className="text-[11px] text-amber-700 mt-1">
              Aktuell überschrieben — zuletzt von <strong>{data.meta.updatedBy}</strong>{' '}
              am {data.meta.updatedAt ? new Date(data.meta.updatedAt).toLocaleString('de-DE') : '—'}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
              if (!window.confirm('Alle bestehenden Anfragen mit den aktuellen Hart-Regeln neu klassifizieren? (kein LLM, kein Coin-Verbrauch)')) return;
              setSaving(true); setFeedback(null);
              try {
                const r = await authFetch('/api/mail-inbound/inquiries/bulk-reclassify', { method: 'POST' });
                const j = await r.json();
                if (!r.ok) throw new Error(j.detail || 'Fehler');
                const detail = Object.entries(j.perKind || {}).map(([k, v]) => `${v}× ${labels.kinds[k] || k}`).join(', ');
                setFeedback({ kind: 'ok', msg: `${j.moved} Anfrage(n) umsortiert${detail ? ' — ' + detail : ''}.` });
              } catch (e) {
                setFeedback({ kind: 'error', msg: String(e.message || e) });
              } finally { setSaving(false); }
            }} disabled={saving}
                  className="text-xs px-3 py-1.5 border border-amber-300 bg-amber-50 rounded text-amber-900 hover:bg-amber-100 inline-flex items-center gap-1.5"
                  data-testid="rules-bulk-reclassify">
            Alle bestehenden Mails neu sortieren
          </button>
          <button onClick={reset} disabled={saving}
                  className="text-xs px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5"
                  data-testid="rules-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Werks-Defaults
          </button>
          <button onClick={save} disabled={!isDirty || saving}
                  className="text-xs px-3 py-1.5 rounded bg-[#9ec043] text-white font-semibold hover:bg-[#8ab035] disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="rules-save">
            <Save className="w-3.5 h-3.5" /> Speichern
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`mb-4 px-3 py-2 rounded text-sm flex items-start gap-2 ${
          feedback.kind === 'ok'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
            : 'bg-rose-50 border border-rose-200 text-rose-900'
        }`} data-testid="rules-feedback">
          {feedback.kind === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          <span>{feedback.msg}</span>
        </div>
      )}

      {/* Sandbox */}
      <section className="bg-sky-50/50 border border-sky-200 rounded-lg p-4 mb-5" data-testid="rules-sandbox">
        <h2 className="text-sm font-semibold text-sky-900 mb-1 flex items-center gap-2">
          <FlaskConical className="w-4 h-4" /> Regel testen
        </h2>
        <p className="text-[11px] text-sky-800/80 mb-3">
          Beispielwerte eintippen → Auswertung gegen alle aktuellen Hart-Regeln (vor LLM).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input type="email" placeholder="Absender E-Mail (z.B. service@paypal.de)"
                 value={test.senderEmail} onChange={(e) => setTest(s => ({ ...s, senderEmail: e.target.value }))}
                 className="px-3 py-2 text-sm rounded border border-sky-200 bg-white"
                 data-testid="test-sender-email" />
          <input type="text" placeholder="Absender Name"
                 value={test.senderName} onChange={(e) => setTest(s => ({ ...s, senderName: e.target.value }))}
                 className="px-3 py-2 text-sm rounded border border-sky-200 bg-white"
                 data-testid="test-sender-name" />
          <input type="text" placeholder="Betreff"
                 value={test.subject} onChange={(e) => setTest(s => ({ ...s, subject: e.target.value }))}
                 className="md:col-span-2 px-3 py-2 text-sm rounded border border-sky-200 bg-white"
                 data-testid="test-subject" />
          <textarea placeholder="Nachrichtentext (optional)"
                    value={test.body} onChange={(e) => setTest(s => ({ ...s, body: e.target.value }))}
                    rows={2}
                    className="md:col-span-2 px-3 py-2 text-sm rounded border border-sky-200 bg-white"
                    data-testid="test-body" />
        </div>
        <button onClick={runTest} disabled={test.loading}
                className="text-xs px-3 py-1.5 rounded bg-sky-600 text-white font-semibold hover:bg-sky-700"
                data-testid="test-run">
          {test.loading ? '…' : 'Regel testen'}
        </button>

        {test.error && (
          <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1">{test.error}</div>
        )}
        {test.result && (
          <div className={`mt-2 px-3 py-2 rounded text-[12px] border ${
            test.result.matched ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                : 'bg-amber-50 border-amber-200 text-amber-900'
          }`} data-testid="test-result">
            {test.result.matched ? (
              <>
                <strong>Match:</strong> Kategorie <strong>{test.result.label}</strong>{' '}
                (Confidence {test.result.confidence}%) — Grund: {test.result.reason}
              </>
            ) : (
              <>Keine Hart-Regel — würde an LLM weitergegeben werden ({test.result.label} als sicherer Default).</>
            )}
          </div>
        )}
      </section>

      {/* Regel-Tabelle */}
      <section className="bg-white border border-gray-200 rounded-lg p-4" data-testid="rules-table">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#32373c]">Aktive Hart-Regeln ({draft.length})</h2>
          <button onClick={addRule}
                  className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1"
                  data-testid="rules-add">
            <Plus className="w-3 h-3" /> Regel hinzufügen
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-[10px] uppercase tracking-wide">
                <th className="text-left font-semibold py-1.5">Feld</th>
                <th className="text-left font-semibold py-1.5 px-2">Pattern (Regex, case-insensitive)</th>
                <th className="text-left font-semibold py-1.5 px-2">→ Kategorie</th>
                <th className="text-left font-semibold py-1.5 px-2">Begründung (Audit)</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {draft.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`rule-row-${i}`}>
                  <td className="py-1">
                    <select value={r.field}
                            onChange={(e) => update(i, { field: e.target.value })}
                            className="text-[12px] px-1.5 py-1 rounded border border-gray-200 bg-white">
                      {Object.entries(labels.fields).map(([k, lbl]) => (
                        <option key={k} value={k}>{lbl}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-2">
                    <input type="text" value={r.pattern}
                           onChange={(e) => update(i, { pattern: e.target.value })}
                           placeholder="@example\.de$"
                           className="w-full text-[12px] font-mono px-2 py-1 rounded border border-gray-200 bg-white" />
                  </td>
                  <td className="py-1 px-2">
                    <select value={r.kind}
                            onChange={(e) => update(i, { kind: e.target.value })}
                            className="text-[12px] px-1.5 py-1 rounded border border-gray-200 bg-white">
                      {Object.entries(labels.kinds).map(([k, lbl]) => (
                        <option key={k} value={k}>{lbl}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-2">
                    <input type="text" value={r.reason || ''}
                           onChange={(e) => update(i, { reason: e.target.value })}
                           placeholder="z.B. PayPal-Absender"
                           className="w-full text-[12px] px-2 py-1 rounded border border-gray-200 bg-white" />
                  </td>
                  <td className="py-1 px-1 text-right">
                    <button onClick={() => remove(i)}
                            className="text-rose-500 hover:text-rose-700"
                            data-testid={`rule-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {!draft.length && (
                <tr><td colSpan={5} className="py-4 text-center text-gray-400 italic">Keine Regeln — alle Mails gehen direkt ans LLM.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
